import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Header } from "./components/Header";
import { BottomNav } from "./components/Nav";
import { RequireCouple, RequireProfile, AdminRedirect, RequireAdmin, EmailVerifiedGuard } from "./components/Guards";
import { PageSkeleton } from "./components/Ui";
import { QuizLayout } from "./components/QuizLayout";
import { InsightLayout } from "./components/InsightLayout";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ChatProvider } from "./contexts/ChatContext";
import { NotificationToast } from "./components/NotificationToast";
import { ReminderProvider } from "./contexts/ReminderContext";
import { PushManager } from "./components/PushManager";
import Landing from "./pages/Landing";
import Onboarding from "./pages/Onboarding";
import Quiz from "./pages/Quiz";
import Profile from "./pages/Profile";
import AuthPage from "./pages/AuthPage";
import VerifyOTP from "./pages/VerifyOTP";
import VerifyEmail from "./pages/VerifyEmail";
import Manage from "./pages/Manage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminSettings from "./pages/AdminSettings";
import { useAuth } from "./hooks/useAuth";

import { TrackingProvider } from "./components/TrackingProvider";
import { PixelScripts } from "./components/PixelScripts";
const AdminTracking = lazy(() => import("./pages/AdminTracking"));
const AdminTrackingSettings = lazy(() => import("./pages/AdminTrackingSettings"));
const AdminPremium = lazy(() => import("./pages/AdminPremium"));
const VisitorDetail = lazy(() => import("./pages/VisitorDetail"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Result = lazy(() => import("./pages/Result"));
const History = lazy(() => import("./pages/History"));
const HistoryDetail = lazy(() => import("./pages/HistoryDetail"));
const Streak = lazy(() => import("./pages/Streak"));
const InsightProfile = lazy(() => import("./pages/InsightProfile"));
const InsightAsk = lazy(() => import("./pages/InsightAsk"));
const ProfileName = lazy(() => import("./pages/ProfileName"));
const ProfilePassword = lazy(() => import("./pages/ProfilePassword"));
const TelegramSettings = lazy(() => import("./pages/TelegramSettings"));
const Notes = lazy(() => import("./pages/Notes"));
const NotesCalendar = lazy(() => import("./pages/NotesCalendar"));
const NoteDayDetail = lazy(() => import("./pages/NoteDayDetail"));
const Chat = lazy(() => import("./pages/Chat"));
const ChatFiles = lazy(() => import("./pages/ChatFiles"));
const ChatSettings = lazy(() => import("./pages/ChatSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WatchRoomPage = lazy(() => import("./pages/WatchRoomPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));
const UploadPage = lazy(() => import("./pages/UploadPage"));
const PlansPage = lazy(() => import("./pages/PlansPage"));
const MoodTimeline = lazy(() => import("./pages/MoodTimeline"));
const StoryList = lazy(() => import("./pages/StoryList"));
const JournalList = lazy(() => import("./pages/JournalList"));
const JournalDetail = lazy(() => import("./pages/JournalDetail"));
const JournalEditor = lazy(() => import("./pages/JournalEditor"));
const ReminderPage = lazy(() => import("./pages/ReminderPage"));
const ReminderDetail = lazy(() => import("./pages/ReminderDetail"));
const PremiumPage = lazy(() => import("./pages/PremiumPage"));
const ReminderForm = lazy(() => import("./pages/ReminderForm"));
const ReferralDashboard = lazy(() => import("./pages/ReferralDashboard"));
const ReferralReferrals = lazy(() => import("./pages/ReferralReferrals"));
const ReferralLedger = lazy(() => import("./pages/ReferralLedger"));
const ReferralBankAccounts = lazy(() => import("./pages/ReferralBankAccounts"));
const ReferralWithdraw = lazy(() => import("./pages/ReferralWithdraw"));
const ReferralVisitors = lazy(() => import("./pages/ReferralVisitors"));
const ShareGenerate = lazy(() => import("./pages/ShareGenerate"));
const InvitePartner = lazy(() => import("./pages/InvitePartner"));
const AdminAffiliate = lazy(() => import("./pages/AdminAffiliate"));
const ExpenseDashboard = lazy(() => import("./pages/ExpenseDashboard"));
const ExpenseLedger = lazy(() => import("./pages/ExpenseLedger"));
const ExpenseForm = lazy(() => import("./pages/ExpenseForm"));
const ExpenseBudget = lazy(() => import("./pages/ExpenseBudget"));
const ExpenseCategories = lazy(() => import("./pages/ExpenseCategories"));
const Assistant = lazy(() => import("./pages/Assistant"));

function Protected({ children, skipProfile }: { children: React.ReactNode; skipProfile?: boolean }) {
	const { isLoaded, isSignedIn } = useAuth();
	if (!isLoaded) {
		return (
			<div style={{ maxWidth: 320, margin: "0 auto", padding: "1.5rem 0" }}>
				<div className="skeleton" style={{ width: "60%", height: "1rem" }} />
			</div>
		);
	}
	if (!isSignedIn) return <Navigate to="/" replace />;
	if (!skipProfile) {
		return <RequireProfile>{children}</RequireProfile>;
	}
	return <>{children}</>;
}

function LandingOrDashboard() {
	const { isLoaded, isSignedIn, session } = useAuth();
	if (!isLoaded) return null;
	if (isSignedIn) {
		if (session?.data?.user?.role === "admin") {
			return <Navigate to="/manage" replace />;
		}
		if (session?.data?.user?.emailVerified === false) {
			return <Navigate to="/verify-email" replace />;
		}
		return <Navigate to="/dashboard" replace />;
	}
	return <Landing />;
}

function ScrollToTop() {
	const { pathname } = useLocation();
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);
	return null;
}

function LazyPage({
	children,
	variant = "default",
}: {
	children: React.ReactNode;
	variant?:
		| "default"
		| "result"
		| "list"
		| "quiz"
		| "streak"
		| "profile"
		| "kenali";
}) {
	return <Suspense fallback={<PageSkeleton variant={variant} />}>{children}</Suspense>;
}

function AppRoutes() {
	return (
		<Routes>
			<Route path="/" element={<LandingOrDashboard />} />
			<Route path="/login" element={<AuthPage key="signin" mode="signin" />} />
			<Route path="/register" element={<AuthPage key="signup" mode="signup" />} />
			<Route path="/verify-otp" element={<VerifyOTP />} />
			<Route path="/verify-email" element={<VerifyEmail />} />

			<Route
				path="/manage"
				element={
					<RequireAdmin>
						<Manage />
					</RequireAdmin>
				}
			>
				<Route index element={<AdminDashboard />} />
				<Route path="users" element={<AdminUsers />} />
				<Route path="settings" element={<AdminSettings />} />
				<Route path="settings/tracking" element={<LazyPage><AdminTrackingSettings /></LazyPage>} />
				<Route path="tracking" element={<LazyPage><AdminTracking /></LazyPage>} />
				<Route path="tracking/analytics" element={<LazyPage><AdminTracking /></LazyPage>} />
				<Route path="tracking/visitors" element={<LazyPage><AdminTracking /></LazyPage>} />
				<Route path="tracking/visitors/:id" element={<LazyPage><VisitorDetail /></LazyPage>} />
				<Route path="premium" element={<LazyPage><AdminPremium /></LazyPage>} />
				<Route path="affiliate" element={<LazyPage><AdminAffiliate /></LazyPage>} />
			</Route>

			<Route
				path="/dashboard"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><Dashboard /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/onboarding"
				element={
					<Protected skipProfile>
						<Onboarding />
					</Protected>
				}
			/>
			<Route
				path="/quiz"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<QuizLayout />
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			>
				<Route index element={<LazyPage variant="quiz"><Quiz /></LazyPage>} />
				<Route path="history" element={<LazyPage variant="list"><History /></LazyPage>} />
				<Route path="history/:date" element={<LazyPage variant="list"><HistoryDetail /></LazyPage>} />
			</Route>
			<Route
				path="/result"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage variant="result"><Result /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/streak"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage variant="streak"><Streak /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/insights"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<InsightLayout />
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			>
				<Route index element={<LazyPage variant="kenali"><InsightProfile /></LazyPage>} />
				<Route path="ask" element={<LazyPage variant="kenali"><InsightAsk /></LazyPage>} />
			</Route>
			<Route
				path="/chat"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage><Chat /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/chat/files"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage><ChatFiles /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/chat/settings"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage><ChatSettings /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/settings"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<Profile />
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/settings/name"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage variant="profile"><ProfileName /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/settings/password"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage variant="profile"><ProfilePassword /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/settings/telegram"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage variant="profile"><TelegramSettings /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/notes"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><Notes /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/notes/calendar"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage variant="streak"><NotesCalendar /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/notes/calendar/:date"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage variant="list"><NoteDayDetail /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>

			{/* ── NOBAR ──────────────────────────────────────────────── */}
			<Route
				path="/nobar/watch"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage><WatchRoomPage /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/nobar/videos"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage><VideosPage /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/nobar/upload"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage><UploadPage /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/nobar/plans"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<RequireCouple>
								<LazyPage><PlansPage /></LazyPage>
							</RequireCouple>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>

			{/* ── Daily Features ───────────────────────────────────────── */}
			<Route
				path="/moods"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><MoodTimeline /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/stories"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><StoryList /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/journals"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><JournalList /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/journals/new"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><JournalEditor /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/journals/:id"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><JournalDetail /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/journals/:id/edit"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><JournalEditor /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/reminders"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReminderPage /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/reminders/new"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReminderForm /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/reminders/date/:date"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReminderDetail /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/reminders/:id/edit"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReminderForm /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>

			{/* ── Expense Tracker ────────────────────────────────────────── */}
			<Route
				path="/expenses/dashboard"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ExpenseDashboard /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/expenses/ledger"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ExpenseLedger /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/expenses/add"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ExpenseForm /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/expenses/edit/:id"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ExpenseForm /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/expenses/budget"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ExpenseBudget /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/expenses/categories"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ExpenseCategories /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>

			{/* ── Assistant ───────────────────────────────────────────── */}
			<Route
				path="/assistant"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><Assistant /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>

			<Route
				path="/premium"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><PremiumPage /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>

			{/* ── Referral / Affiliate ──────────────────────────────────── */}
			<Route
				path="/referral"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReferralDashboard /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/referral/referrals"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReferralReferrals /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/referral/ledger"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReferralLedger /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/referral/bank-accounts"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReferralBankAccounts /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/referral/withdraw"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReferralWithdraw /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/referral/visitors"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ReferralVisitors /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/share/generate"
				element={
					<Protected>
						<EmailVerifiedGuard>
							<LazyPage><ShareGenerate /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>
			<Route
				path="/invite"
				element={
					<Protected skipProfile>
						<EmailVerifiedGuard>
							<LazyPage><InvitePartner /></LazyPage>
						</EmailVerifiedGuard>
					</Protected>
				}
			/>

			<Route path="*" element={<LazyPage><NotFound /></LazyPage>} />
		</Routes>
	);
}

function Layout() {
	const { pathname } = useLocation();
	const isAdminArea = pathname.startsWith("/manage");
	const isChatArea = pathname.startsWith("/chat");
	const hideChrome =
		isAdminArea ||
		isChatArea ||
		pathname === "/login" ||
		pathname === "/register" ||
		pathname === "/verify-otp" ||
		pathname === "/verify-email" ||
		pathname === "/onboarding" ||
		pathname === "/invite";

	const layoutContent = (
		<div className="flex min-h-screen flex-col bg-[color:var(--color-bg)]">
			<ScrollToTop />
			{!hideChrome && <Header />}
			<main
				className={`mx-auto w-full max-w-[480px] flex-1 px-4${hideChrome ? " flex flex-col" : " pt-4"}`}
				style={{
					paddingBottom: hideChrome
						? "env(safe-area-inset-bottom)"
						: "max(7rem, calc(7rem + env(safe-area-inset-bottom)))",
				}}
			>
				<AdminRedirect>
					<AppRoutes />
				</AdminRedirect>
			</main>
			{!hideChrome && <BottomNav />}
		</div>
	);

	return (
		<NotificationProvider>
			<ReminderProvider>
				<TrackingProvider>
					<PixelScripts />
					{isChatArea ? <ChatProvider>{layoutContent}</ChatProvider> : layoutContent}
					<NotificationToast />
					<PushManager />
				</TrackingProvider>
			</ReminderProvider>
		</NotificationProvider>
	);
}

export default function App() {
	return (
		<BrowserRouter>
			<Layout />
		</BrowserRouter>
	);
}
